import type {Ability} from '../../../../generated/types';
import {isAbility, SKILLS} from './abilities';
import {ChoiceInstance, FeatureInstance, FeatureOwner, flattenChoices} from './feature-tree';
import {evaluateCount, evaluateFormula, evaluateNumber, FormulaContext, resolveReference} from './formula';
import {evaluateProcedure, Procedure} from './procedures';
import type {PropsSource} from './props-sources';
import {
	ACTION_KINDS,
	ActionProps,
	ActionKind,
	AttackBonusProps,
	RollBonus,
	RollModeEffect,
	EXTRA_SPEEDS,
	ExtraSpeed,
	FeatureProps,
	FormulaValue,
	Recharge,
	Sense,
	SENSES,
	SpellcastingMode,
	SpellTable,
} from './types';

export type FormulaContextFactory = (owner: FeatureOwner, scopes: string[]) => FormulaContext;

export type CharacterSpell = {
	id: string;
	/** выбрано в подготовленные: список меняется по правилам класса */
	prepared: boolean;
	/** всегда подготовлено: заклинание даёт особенность */
	alwaysPrepared: boolean;
	/** взято из книги или списка класса и готово только после подготовки */
	needsPreparation: boolean;
	ability?: string;
	usage?: FormulaValue;
	recharge?: Recharge;
	/** особенность, которая дала заклинание; у заклинаний из списка класса не задаётся */
	source?: string;
	/** классы, через которые получено заклинание: по ним оно попадает во вкладку класса в управлении */
	classIds: string[];
	/** вписано в книгу на листе, а не выбором класса: его можно убрать из книги */
	addedToBook?: boolean;
	/** ключ ресурса, который тратит накладывание: заряды предмета */
	resourceKey?: string;
	cost?: number;
	saveDc?: number;
	attackBonus?: number;
	/** способы наложения: ячейкой, своими использованиями, зарядами предмета */
	castings: SpellCasting[];
};

export type SpellCasting = {
	key: string;
	/** slot — ячейкой заклинаний, uses — использования черты или вида, resource — заряды предмета */
	kind: 'slot' | 'uses' | 'resource';
	/** особенность или предмет, который дал этот способ */
	source?: string;
	ability?: string;
	usage?: FormulaValue;
	recharge?: Recharge;
	resourceKey?: string;
	cost?: number;
	saveDc?: number;
	attackBonus?: number;
};

function spellCastings(spell: Omit<CharacterSpell, 'castings'>): SpellCasting[] {
	const {id, source, ability, usage, recharge, resourceKey, cost, saveDc, attackBonus} = spell;
	const slot: SpellCasting = {key: `slot:${id}`, kind: 'slot', ability};
	if (resourceKey) {
		return [
			{key: `${resourceKey}:${id}`, kind: 'resource', source, ability, resourceKey, cost, saveDc, attackBonus},
		];
	}
	if (usage != null) return [{key: `spell:${id}`, kind: 'uses', source, ability, usage, recharge}, slot];
	return [slot];
}

type SpellOrigin = {
	/** особенность, которая дала заклинание; у заклинаний из списка класса не задаётся */
	source?: string;
	/** выбор сделан в классе с книгой заклинаний: заклинание попадает в книгу */
	isSpellbook: boolean;
	/** выбор подготовки из списка класса или книги */
	preparesFromList: boolean;
};

export type SpellcastingSource = {
	title: string;
	/** класс-владелец особенности */
	classId?: string;
	/** чей список заклинаний */
	spellList: string;
	ability?: Ability;
	mode: SpellcastingMode;
	/** заклинательство даёт подкласс: при мультиклассе засчитывается треть уровней */
	fromSubClass: boolean;
	/** своя таблица заклинательства подкласса */
	table?: SpellTable;
};

export type CharacterResource = {
	/** ключ для учёта потраченного */
	key: string;
	id: string;
	title: string;
	/** экземпляр особенности, которая дала ресурс */
	featureKey: string;
	max?: FormulaValue;
	die?: FormulaValue;
	recharge?: Recharge;
	regainsOnShortRest?: number;
	/** сколько возвращается при восстановлении: `1d6+1` */
	regain?: string;
};

export type CharacterAction = {
	/** ключ для учёта потраченных использований */
	key: string;
	/** вид действия; без него — особенность с ограниченным числом использований */
	kind?: ActionKind;
	title: string;
	/** экземпляр особенности, которая дала действие */
	featureKey: string;
	usage?: FormulaValue;
	recharge?: Recharge;
	/** сколько использований возвращает короткий отдых */
	regainsOnShortRest?: number;
	resource?: string;
	/** урон действия: формула костей с бонусом */
	damage?: string;
	damageType?: string;
	/** испытание против действия с посчитанной СЛ */
	save?: {ability: Ability; dc: number};
	area?: string;
	/** бросок лечения тем же действием */
	healing?: string;
	/** применение бросает кость ресурса */
	rollsResourceDie?: boolean;
	/** окно выбора по кнопке «Применить» */
	procedure?: Procedure;
};

export type CombatNoteKind = 'critRange' | 'unarmedDamageDie' | 'damageBonus' | 'tempHP' | 'heroicInspiration';

export type CombatNote = {
	kind: CombatNoteKind;
	title: string;
	value?: FormulaValue;
};

/** слагаемое КБ от особенности или предмета */
export type ArmorClassBonus = {
	title: string;
	value: number;
};

export type UnarmoredDefenseEffect = {
	/** особенность, которая даёт защиту без доспехов */
	title: string;
	base: number;
	abilities: Ability[];
	withoutShield: boolean;
};

export type CharacterEffects = {
	savingThrows: Set<string>;
	skills: Set<string>;
	expertise: Set<string>;
	armor: Set<string>;
	weapons: Set<string>;
	tools: Set<string>;
	instruments: Set<string>;
	languages: Set<string>;
	weaponMasteries: Set<string>;
	savingThrowAdvantages: Set<string>;
	conditionAdvantages: Set<string>;
	resistances: Set<string>;
	immunities: Set<string>;
	vulnerabilities: Set<string>;
	conditionImmunities: Set<string>;
	senses: Partial<Record<Sense, number>>;
	extraSpeeds: Partial<Record<ExtraSpeed, number>>;
	armorClassBonuses: ArmorClassBonus[];
	/** бонусы к КБ, пока надет доспех */
	armoredArmorClassBonuses: ArmorClassBonus[];
	unarmoredDefenses: UnarmoredDefenseEffect[];
	hitPointsBonus: number;
	hitPointsPerLevel: number;
	extraAttacks: number;
	unarmedDexterity: boolean;
	spellcasting: SpellcastingSource[];
	spells: CharacterSpell[];
	resources: CharacterResource[];
	actions: CharacterAction[];
	notes: CombatNote[];
	savingThrowBonus: number;
	abilityCheckBonus: number;
	spellAttackBonus: number;
	spellSaveDcBonus: number;
	proficiencyBonus: number;
	unarmedStrike: Required<AttackBonusProps>;
	speedMultiplier: number;
	carryingSizeBonus: number;
	rollBonuses: Sourced<RollBonus>[];
	rollModes: Sourced<RollModeEffect>[];
	/** откуда взялось значение списка: `${эффект}:${значение}` → названия особенностей и предметов */
	listSources: Map<string, string[]>;
};

export type Sourced<T> = T & {title: string};

type ListProp =
	| 'savingThrowProficiency'
	| 'skillProficiencies'
	| 'armorProficiencies'
	| 'weaponProficiencies'
	| 'toolsProficiencies'
	| 'musicalInstrumentProficiencies'
	| 'languages'
	| 'savingThrowAdvantage'
	| 'savingThrowConditionAdvantage'
	| 'resists'
	| 'immunities'
	| 'vulnerabilities'
	| 'conditionImmunities';

type SetEffect = {
	[Name in keyof CharacterEffects]: CharacterEffects[Name] extends Set<string> ? Name : never;
}[keyof CharacterEffects];

const LIST_PROPS: Record<ListProp, SetEffect> = {
	savingThrowProficiency: 'savingThrows',
	skillProficiencies: 'skills',
	armorProficiencies: 'armor',
	weaponProficiencies: 'weapons',
	toolsProficiencies: 'tools',
	musicalInstrumentProficiencies: 'instruments',
	languages: 'languages',
	savingThrowAdvantage: 'savingThrowAdvantages',
	savingThrowConditionAdvantage: 'conditionAdvantages',
	resists: 'resistances',
	immunities: 'immunities',
	vulnerabilities: 'vulnerabilities',
	conditionImmunities: 'conditionImmunities',
};

const SPEED_PROPS: Record<ExtraSpeed, keyof FeatureProps> = {
	fly: 'flySpeed',
	swim: 'swimSpeed',
	climb: 'climbSpeed',
	burrow: 'burrowSpeed',
};

const NOTE_PROPS = ['critRange', 'unarmedDamageDie', 'damageBonus', 'tempHP'] as const;

export const createEmptyEffects = (): CharacterEffects => ({
	savingThrows: new Set(),
	skills: new Set(),
	expertise: new Set(),
	armor: new Set(),
	weapons: new Set(),
	tools: new Set(),
	instruments: new Set(),
	languages: new Set(),
	weaponMasteries: new Set(),
	savingThrowAdvantages: new Set(),
	conditionAdvantages: new Set(),
	resistances: new Set(),
	immunities: new Set(),
	vulnerabilities: new Set(),
	conditionImmunities: new Set(),
	senses: {},
	extraSpeeds: {},
	armorClassBonuses: [],
	armoredArmorClassBonuses: [],
	unarmoredDefenses: [],
	hitPointsBonus: 0,
	hitPointsPerLevel: 0,
	extraAttacks: 0,
	unarmedDexterity: false,
	spellcasting: [],
	spells: [],
	resources: [],
	actions: [],
	notes: [],
	savingThrowBonus: 0,
	abilityCheckBonus: 0,
	spellAttackBonus: 0,
	spellSaveDcBonus: 0,
	proficiencyBonus: 0,
	unarmedStrike: {attackBonus: 0, damageBonus: 0},
	speedMultiplier: 1,
	carryingSizeBonus: 0,
	rollBonuses: [],
	rollModes: [],
	listSources: new Map(),
});

export function collectEffects(
	features: FeatureInstance[],
	propsSources: PropsSource[],
	contextFor: FormulaContextFactory,
	spellbookSpells: Record<string, string[]> = {},
): CharacterEffects {
	const effects = createEmptyEffects();
	for (const source of propsSources) addProps(effects, source, contextFor(source.owner, source.scopes));
	const spellbookOwners = new Set(
		features
			.filter((instance) => instance.feature.props?.spellcasting?.mode === 'spellbook')
			.map((instance) => instance.owner.key),
	);
	for (const instance of features) {
		const context = contextFor(instance.owner, [instance.key, instance.owner.key]);
		const spellcastingMode = instance.feature.props?.spellcasting?.mode;
		const origin: SpellOrigin = {
			source: spellcastingMode ? undefined : instance.feature.title,
			isSpellbook: spellbookOwners.has(instance.owner.key),
			preparesFromList: spellcastingMode === 'prepared' || spellcastingMode === 'spellbook',
		};
		for (const choice of flattenChoices(instance.choices)) addChoice(effects, choice, origin, context);
		const {classId} = instance.owner;
		if (spellcastingMode === 'spellbook' && classId) {
			for (const spellId of spellbookSpells[classId] || []) {
				addSpell(effects, {
					id: spellId,
					prepared: false,
					alwaysPrepared: false,
					needsPreparation: true,
					classIds: [classId],
					addedToBook: true,
				});
			}
		}
	}
	return effects;
}

function addProps(effects: CharacterEffects, source: PropsSource, context: FormulaContext) {
	const {props, title} = source;
	const resolve = (value: string) => resolveReference(value, context);

	for (const [propName, effectName] of Object.entries(LIST_PROPS) as [ListProp, SetEffect][]) {
		for (const value of props[propName] || []) {
			const resolved = resolve(value);
			effects[effectName].add(resolved);
			const sourceKey = `${effectName}:${resolved}`;
			const titles = effects.listSources.get(sourceKey) || [];
			if (title && !titles.includes(title)) effects.listSources.set(sourceKey, [...titles, title]);
		}
	}

	for (const sense of SENSES) {
		const range = evaluateNumber(props[sense], context);
		if (range) effects.senses[sense] = Math.max(effects.senses[sense] || 0, range);
	}

	for (const speed of EXTRA_SPEEDS) {
		const value = evaluateNumber(props[SPEED_PROPS[speed]] as FormulaValue | undefined, context);
		if (value) effects.extraSpeeds[speed] = Math.max(effects.extraSpeeds[speed] || 0, value);
	}

	const armorClassBonus = evaluateNumber(props.acBonus, context) || 0;
	if (armorClassBonus) effects.armorClassBonuses.push({title, value: armorClassBonus});
	const armoredArmorClassBonus = evaluateNumber(props.armoredAcBonus, context) || 0;
	if (armoredArmorClassBonus) effects.armoredArmorClassBonuses.push({title, value: armoredArmorClassBonus});
	effects.hitPointsBonus += evaluateNumber(props.hpMax, context) || 0;
	effects.hitPointsPerLevel += evaluateNumber(props.hpIncrementEveryLevel, context) || 0;
	effects.extraAttacks = Math.max(effects.extraAttacks, props.extraAttack || 0);
	effects.unarmedDexterity ||= !!props.unarmedDexterity;
	effects.savingThrowBonus += evaluateNumber(props.savingThrowBonus, context) || 0;
	effects.abilityCheckBonus += evaluateNumber(props.abilityCheckBonus, context) || 0;
	effects.spellAttackBonus += evaluateNumber(props.spellAttackBonus, context) || 0;
	effects.spellSaveDcBonus += evaluateNumber(props.spellSaveDcBonus, context) || 0;
	effects.proficiencyBonus += props.proficiencyBonus || 0;
	effects.unarmedStrike.attackBonus += props.unarmedStrike?.attackBonus || 0;
	effects.unarmedStrike.damageBonus += props.unarmedStrike?.damageBonus || 0;
	effects.speedMultiplier = Math.max(effects.speedMultiplier, props.speedMultiplier || 1);
	effects.carryingSizeBonus = Math.max(effects.carryingSizeBonus, props.carryingSizeBonus || 0);
	const resolveTarget = <T extends {ability?: string; skill?: string}>(effect: T): T => ({
		...effect,
		ability: effect.ability && resolve(effect.ability),
		skill: effect.skill && resolve(effect.skill),
	});
	effects.rollBonuses.push(...(props.rollBonus || []).map((bonus) => ({...resolveTarget(bonus), title})));
	effects.rollModes.push(...(props.rollMode || []).map((mode) => ({...resolveTarget(mode), title})));

	if (props.unarmoredDefense) {
		effects.unarmoredDefenses.push({
			title,
			base: props.unarmoredDefense.base,
			abilities: props.unarmoredDefense.abilities.map(resolve).filter(isAbility),
			withoutShield: !!props.unarmoredDefense.withoutShield,
		});
	}

	if (props.spellcasting) {
		const ability = resolve(props.spellcasting.ability);
		effects.spellcasting.push({
			title,
			classId: source.owner.classId,
			spellList: props.spellcasting.class,
			ability: isAbility(ability) ? ability : undefined,
			mode: props.spellcasting.mode,
			fromSubClass: !!source.feature.subClassId,
			table: props.spellcasting.table,
		});
	}

	for (const spell of props.spells || []) {
		addSpell(effects, {
			id: spell.spell,
			prepared: false,
			alwaysPrepared: !!spell.prepared,
			needsPreparation: false,
			ability: spell.ability ? resolve(spell.ability) : undefined,
			usage: evaluateFormula(spell.usage, context),
			recharge: spell.recharge,
			source: title,
			classIds: source.owner.classId ? [source.owner.classId] : [],
			resourceKey: spell.resource ? `resource:${spell.resource}` : undefined,
			cost: spell.cost,
			saveDc: spell.saveDc,
			attackBonus: spell.attackBonus,
		});
	}

	for (const resource of props.resources || []) {
		const evaluated: CharacterResource = {
			...resource,
			key: `resource:${resource.id}`,
			title,
			featureKey: source.featureKey,
			max: evaluateCount(resource.max, context, resource.min),
			die: evaluateFormula(resource.die, context),
		};
		const index = effects.resources.findIndex((existing) => existing.id === resource.id);
		if (index === -1) effects.resources.push(evaluated);
		else
			effects.resources[index] = {
				...effects.resources[index],
				...withoutEmpty(evaluated),
				title: effects.resources[index].title,
				featureKey: effects.resources[index].featureKey,
			};
	}

	for (const kind of ACTION_KINDS) {
		const action = props[kind];
		if (action) {
			effects.actions.push({
				key: `${source.key}:${kind}`,
				kind,
				title,
				featureKey: source.featureKey,
				usage: evaluateCount(action.usage, context, action.min),
				recharge: action.recharge,
				regainsOnShortRest: action.regainsOnShortRest,
				resource: action.resource,
				...actionEffect(action, context),
			});
		}
	}
	if (props.usage != null || props.recharge) {
		effects.actions.push({
			key: `${source.key}:uses`,
			title,
			featureKey: source.featureKey,
			usage: evaluateCount(props.usage, context, props.usageMin),
			recharge: props.recharge,
		});
	}

	for (const kind of NOTE_PROPS) {
		if (props[kind] != null) effects.notes.push({kind, title, value: evaluateFormula(props[kind], context)});
	}
	if (props.afterLongRest?.heroicInspiration) effects.notes.push({kind: 'heroicInspiration', title});
}

/** ссылка на выбор, который ещё не сделан, остаётся без значения, а не строкой `{draconicAncestryDamageType}` */
const resolvedValue = (value: string | undefined, context: FormulaContext) => {
	const resolved = value && resolveReference(value, context);
	return resolved && !resolved.startsWith('{') ? resolved : undefined;
};

function actionEffect(action: ActionProps, context: FormulaContext): Partial<CharacterAction> {
	const damage = evaluateFormula(action.damage, context);
	const healing = evaluateFormula(action.healing, context);
	const dc = action.save && evaluateNumber(action.save.dc, context);
	const ability = action.save && resolveReference(action.save.ability, context);
	return withoutEmpty({
		damage: damage == null ? undefined : String(damage),
		damageType: resolvedValue(action.damageType, context),
		save: ability && isAbility(ability) && dc != null ? {ability, dc} : undefined,
		area: action.area,
		healing: healing == null ? undefined : String(healing),
		rollsResourceDie: action.rollsResourceDie || undefined,
		procedure: action.procedure && evaluateProcedure(action.procedure, context),
	});
}

function addChoice(effects: CharacterEffects, choice: ChoiceInstance, origin: SpellOrigin, context: FormulaContext) {
	const {group, selected} = choice;
	if (group.type === 'expertise') selected.forEach((skill) => effects.expertise.add(skill));
	else if (group.type === 'skill') selected.forEach((skill) => effects.skills.add(skill));
	else if (group.type === 'tool' || group.type === 'gamingSet') selected.forEach((tool) => effects.tools.add(tool));
	else if (group.type === 'instrument') selected.forEach((instrument) => effects.instruments.add(instrument));
	else if (group.type === 'language') selected.forEach((language) => effects.languages.add(language));
	else if (group.type === 'weapon') selected.forEach((weapon) => effects.weaponMasteries.add(weapon));
	else if (group.type === 'proficiency') {
		selected.forEach((key) => (SKILLS.includes(key) ? effects.skills : effects.tools).add(key));
	} else if (group.type === 'spell') {
		const {prepared, spellcastingAbility, usage, min, recharge} = group.params;
		for (const spellId of selected) {
			addSpell(effects, {
				id: spellId,
				prepared: !!prepared,
				alwaysPrepared: false,
				needsPreparation: origin.isSpellbook || origin.preparesFromList,
				ability: spellcastingAbility ? resolveReference(spellcastingAbility, context) : undefined,
				usage: evaluateCount(usage, context, min),
				recharge,
				source: origin.source,
				classIds: choice.owner.classId ? [choice.owner.classId] : [],
			});
		}
	}
}

function addSpell(effects: CharacterEffects, added: Omit<CharacterSpell, 'castings'>) {
	const spell: CharacterSpell = {...added, castings: spellCastings(added)};
	const index = effects.spells.findIndex((existing) => existing.id === spell.id);
	if (index === -1) {
		effects.spells.push(spell);
		return;
	}
	const existing = effects.spells[index];
	const castingKeys = new Set(existing.castings.map((casting) => casting.key));
	effects.spells[index] = {
		...existing,
		castings: [...existing.castings, ...spell.castings.filter((casting) => !castingKeys.has(casting.key))],
		prepared: existing.prepared || spell.prepared,
		alwaysPrepared: existing.alwaysPrepared || spell.alwaysPrepared,
		needsPreparation: existing.needsPreparation && spell.needsPreparation,
		classIds: [...new Set([...existing.classIds, ...spell.classIds])],
		addedToBook: existing.addedToBook || spell.addedToBook,
		ability: existing.ability ?? spell.ability,
		usage: existing.usage ?? spell.usage,
		recharge: existing.recharge ?? spell.recharge,
	};
}

const withoutEmpty = <T extends object>(value: T): Partial<T> =>
	Object.fromEntries(Object.entries(value).filter(([, entry]) => entry != null)) as Partial<T>;
